import React, { memo, useEffect, useState } from "react";
import { PaginationInfo } from './Helpers.jsx';
import PropTypes from "prop-types";
import styles from "./Table.module.scss";

const PaginationControls = ({
  listCount = 0,
  isLoading,
  curentPageNumber,
  pageLimit,
  gotoPage,
  onPageNoChange,
  previousPage,
  nextPage,
  setPageSize,
  onPageLimitChange,
}) => {
  if (listCount <= 0) {
    return null;
  }

  const [pageInput, setPageInput] = useState(curentPageNumber + 1);
  const sizeOfPage = pageLimit !== undefined ? pageLimit : 10;
  const [pageSizeInput, setPageSizeInput] = useState(sizeOfPage);

  const totalPageCount = listCount ? Math.ceil(listCount / pageLimit) : 0;
  const isLastPage = curentPageNumber === totalPageCount - 1;
  const showFullPagination = totalPageCount > 1;

  const handlePreviousClick = () => {
    if (curentPageNumber > 0) {
      const newPageNumber = curentPageNumber - 1;
      previousPage();
      onPageNoChange(newPageNumber);
      setPageInput(pageInput);
    }
  };

  const handleNextClick = () => {
    const newPageNumber = curentPageNumber + 1;
    nextPage();
    onPageNoChange(newPageNumber);
    setPageInput(pageInput);
  };

  const handlePageSizeChange = (newPageSize) => {
    gotoPage(0);
    onPageLimitChange(newPageSize);
  };

  const handleInputChangePage = (e) => {
    if (e.target.value <= totalPageCount) {
      setPageInput(e.target.value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const newPage = parseInt(e.target.value, 10);
      if (!isNaN(newPage) && newPage > 0 && newPage <= totalPageCount) {
        gotoPage(newPage - 1);
        onPageNoChange(newPage - 1);
      } else {
        setPageInput(curentPageNumber + 1);
      }
    }
  };

  const handleBlurPageInput = () => {
    if (pageInput < curentPageNumber + 1) {
      setPageInput(curentPageNumber + 1);
    }
  };

  useEffect(() => {
    setPageInput(curentPageNumber + 1);
  }, [curentPageNumber]);

  const handleInputChangePageSize = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && Number(value) <= 100) {
      setPageSizeInput(value);
    }
  };

  const handleKeyPressPagesize = (e) => {
    if (e.key === "Enter") {
      const newSize = Number(pageSizeInput);
      if (newSize >= 1 && newSize <= 100) {
        setPageSize(newSize);
        handlePageSizeChange(newSize);
        gotoPage(0);
      }
    }
  };

  useEffect(() => {
    setPageSizeInput(sizeOfPage);
  }, [pageLimit, sizeOfPage]);

  const handleBlurPageSize = () => {
    const newSize = Number(pageSizeInput);
    if (!newSize || newSize < 1 || newSize > 100) {
      setPageSizeInput(sizeOfPage);
    } else {
      setPageSize(newSize);
      handlePageSizeChange(newSize);
    }
  };

  const handleFirstPageClick = () => {
    setPageInput(1);
    gotoPage(0);
    onPageNoChange(0);
  };

  const handleLastPageClick = () => {
    setPageInput(totalPageCount);
    gotoPage(totalPageCount - 1);
    onPageNoChange(totalPageCount - 1);
  };

  return (
    <div className="d-flex justify-content-between align-items-center mb-0">
      {!isLoading && (
        <div>
          <PaginationInfo
            currentPage={curentPageNumber}
            pageSize={sizeOfPage}
            listCount={listCount}
          />
        </div>
      )}

      {!isLoading && showFullPagination && (
        <div className="d-flex justify-content-center">
          <div className="d-flex align-items-center">
            <div className="pe-3">
              <button
                onClick={handleFirstPageClick}
                className={`${styles["far-back-icon"]} ${pageInput === 1 ? "disabled-icon" : ""
                  } ${pageInput === totalPageCount
                    ? "cursor-default"
                    : "cursor-pointer"
                  }`}
                  data-testid="first-page-button"
                disabled={curentPageNumber === 0}
              >
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.400819 0.00162569H2.44586C2.55434 0.00162569 2.65837 0.0447184 2.73507 0.121424C2.81178 0.19813 2.85487 0.302165 2.85487 0.410643V3.73595L8.35603 0.0547979C8.4182 0.0188992 8.48874 0 8.56053 0C8.63233 0 8.70286 0.0188992 8.76504 0.0547979C8.83127 0.0881475 8.88718 0.138876 8.92681 0.201555C8.96643 0.264235 8.98827 0.336508 8.98999 0.410643V8.59098C8.98979 8.66498 8.96953 8.73753 8.93135 8.80091C8.89318 8.8643 8.83853 8.91614 8.77322 8.95092C8.70926 8.98376 8.63777 8.99916 8.56597 8.99557C8.49416 8.99198 8.42457 8.96952 8.36421 8.93047L2.86305 5.24931V8.59098C2.86305 8.69946 2.81996 8.8035 2.74325 8.8802C2.66655 8.95691 2.56252 9 2.45404 9H0.408998C0.300523 9 0.19649 8.95691 0.119786 8.8802C0.0430822 8.8035 -9.53674e-06 8.69946 -9.53674e-06 8.59098V0.410643C-3.05176e-05 0.303567 0.0419369 0.200753 0.11688 0.124278C0.191824 0.0478035 0.293767 0.00376719 0.400819 0.00162569Z"
                    fill="#939393"
                  />
                </svg>
              </button>
            </div>

            <div className="pe-3">
              <button
                className={`${styles["far-back-icon"]} ${pageInput === 1 ? "disabled-icon" : ""
                  } ${pageInput === totalPageCount
                    ? "cursor-default"
                    : "cursor-pointer"
                  }`}
                  data-testid="previous-page-button"
                onClick={handlePreviousClick}
                disabled={curentPageNumber === 0}
              >
                <svg
                  width="13"
                  height="9"
                  viewBox="0 0 13 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.28873 3.68394L6.1982 0.0838961C6.28792 0.0298853 6.39012 0.000947745 6.49442 2.28866e-05C6.59872 -0.000901972 6.70141 0.0262187 6.79204 0.0786304C6.88268 0.131042 6.95803 0.206877 7.01046 0.298431C7.06289 0.389986 7.09051 0.493998 7.09053 0.599902V3.13793L12.1077 0.0838961C12.1974 0.0298853 12.2996 0.000947745 12.4039 2.28866e-05C12.5082 -0.000901972 12.6109 0.0262187 12.7015 0.0786304C12.7921 0.131042 12.8675 0.206877 12.9199 0.298431C12.9724 0.389986 13 0.493998 13 0.599902V8.39999C13.0002 8.51017 12.9706 8.61828 12.9143 8.71248C12.858 8.80667 12.7772 8.88331 12.6809 8.93399C12.5968 8.97775 12.5036 9.00038 12.4091 9C12.2886 8.99971 12.171 8.96202 12.0722 8.89199L7.09053 5.35195V8.39999C7.09075 8.51017 7.0611 8.61828 7.00481 8.71248C6.94852 8.80667 6.86777 8.88331 6.77142 8.93399C6.68731 8.97775 6.59409 9.00038 6.49958 9C6.37908 8.99971 6.26155 8.96202 6.16274 8.89199L0.253273 4.69195C0.172163 4.63461 0.106513 4.55753 0.0623312 4.46776C0.0181503 4.378 -0.00315189 4.27841 0.000375748 4.17813C0.00390339 4.07784 0.0321474 3.98006 0.0825233 3.89372C0.132899 3.80739 0.203797 3.73526 0.28873 3.68394Z"
                    fill="#939393"
                  />
                </svg>
              </button>
            </div>

            <div className="page">Page</div>

            <div className={styles.rectangle}>
              <input
                className={styles.rectangle}
                type="number"
                value={pageInput}
                min={1} // Ensure the minimum value is 1
                max={totalPageCount}
                onChange={handleInputChangePage}
                onKeyPress={handleKeyPress}
                onBlur={handleBlurPageInput}
                data-testid="page-input"
              />
            </div>

            <div className={` ${styles.pagestyle} ${styles["of-1"]}`}>of {totalPageCount}</div>

            <div className={`ps-3 ${styles.pagestyle}`}>
              <button
                className={`${styles["far-back-icon"]} ${pageInput === totalPageCount ? "disabled-icon" : ""
                  } ${pageInput === totalPageCount
                    ? "cursor-default"
                    : "cursor-pointer"
                  }`}
                  data-testid="next-page-button"
                onClick={handleNextClick}
                disabled={isLastPage}
              >
                <svg
                  width="13"
                  height="9"
                  viewBox="0 0 13 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.7113 3.68394L6.8018 0.0838961C6.71208 0.0298853 6.60988 0.000947745 6.50558 2.28866e-05C6.40128 -0.000901972 6.29859 0.0262187 6.20796 0.0786304C6.11732 0.131042 6.04197 0.206877 5.98954 0.298431C5.93711 0.389986 5.90949 0.493998 5.90947 0.599902V3.13793L0.892331 0.0838961C0.802611 0.0298853 0.70041 0.000947745 0.596109 2.28866e-05C0.491808 -0.000901972 0.389125 0.0262187 0.298489 0.0786304C0.207854 0.131042 0.132496 0.206877 0.08007 0.298431C0.0276445 0.389986 1.94686e-05 0.493998 1.27873e-06 0.599902V8.39999C-0.000224428 8.51017 0.0294334 8.61828 0.085723 8.71248C0.142013 8.80667 0.22276 8.88331 0.319113 8.93399C0.403223 8.97775 0.496442 9.00038 0.590948 9C0.711447 8.99971 0.828979 8.96202 0.927788 8.89199L5.90947 5.35195V8.39999C5.90925 8.51017 5.9389 8.61828 5.99519 8.71248C6.05148 8.80667 6.13223 8.88331 6.22858 8.93399C6.31269 8.97775 6.40591 9.00038 6.50042 9C6.62092 8.99971 6.73845 8.96202 6.83726 8.89199L12.7467 4.69195C12.8278 4.63461 12.8935 4.55753 12.9377 4.46776C12.9818 4.378 13.0032 4.27841 12.9996 4.17813C12.9961 4.07784 12.9679 3.98006 12.9175 3.89372C12.8671 3.80739 12.7962 3.73526 12.7113 3.68394Z"
                    fill="#939393"
                  />
                </svg>
              </button>
            </div>

            <div className={`ps-3 ${styles.pagestyle}`}>
              <button
                onClick={handleLastPageClick}
                className={`${styles["far-back-icon"]} ${pageInput === totalPageCount ? "disabled-icon" : ""
                  } ${pageInput === totalPageCount
                    ? "cursor-default"
                    : "cursor-pointer"
                  }`}
                  data-testid="last-page-button"
                disabled={isLastPage}
              >
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.58917 0.00162569H6.54413C6.43566 0.00162569 6.33162 0.0447184 6.25492 0.121424C6.17821 0.19813 6.13512 0.302165 6.13512 0.410643V3.73595L0.633963 0.0547979C0.571786 0.0188992 0.501254 0 0.429459 0C0.357663 0 0.287132 0.0188992 0.224954 0.0547979C0.158724 0.0881475 0.102805 0.138876 0.063181 0.201555C0.0235568 0.264235 0.00171766 0.336508 0 0.410643V8.59098C0.000197432 8.66498 0.0204627 8.73753 0.0586369 8.80091C0.0968111 8.8643 0.151464 8.91614 0.216774 8.95092C0.280732 8.98376 0.352216 8.99916 0.424022 8.99557C0.495828 8.99198 0.56542 8.96952 0.625782 8.93047L6.12694 5.24931V8.59098C6.12694 8.69946 6.17003 8.8035 6.24674 8.8802C6.32344 8.95691 6.42747 9 6.53595 9H8.58099C8.68947 9 8.7935 8.95691 8.8702 8.8802C8.94691 8.8035 8.99 8.69946 8.99 8.59098V0.410643C8.99002 0.303567 8.94805 0.200753 8.87311 0.124278C8.79817 0.0478035 8.69622 0.00376719 8.58917 0.00162569Z"
                    fill="#939393"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="">
          <ul className={`${styles.checkStyle} mt-4`}>
            <li className={styles.rowsPerPage}>
              <label className="page text-prewrap" data-testid="rowsPerPageLabel" htmlFor="page-size-select">
                Rows per page{" "}
              </label>

              <div className={`form-group mb-1 ${styles["inline-page-input"]}`} data-testid="rowsPerPageInputDiv">
                <input
                  className={styles["rectangle-Rows"]}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pageSizeInput}
                  min="1"
                  max="100"
                  onChange={handleInputChangePageSize}
                  onKeyPress={handleKeyPressPagesize}
                  onBlur={handleBlurPageSize}
                />
              </div>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

PaginationControls.propTypes = {
  listCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  curentPageNumber: PropTypes.number.isRequired,
  pageLimit: PropTypes.number,
  gotoPage: PropTypes.func.isRequired,
  onPageNoChange: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  onPageLimitChange: PropTypes.func.isRequired,
};
PaginationControls.defaultProps = {
  listCount: 0,
  isLoading: false,
  curentPageNumber: 0,
  pageLimit: 10,
  gotoPage: () => { },
  onPageNoChange: () => { },
  previousPage: () => { },
  nextPage: () => { },
  setPageSize: () => { },
  onPageLimitChange: () => {},
};
export default memo(PaginationControls);


